// src/pages/SuperAdmin/AdminWhatsapp.tsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { fetchContatosWhatsapp, fetchMensagensContato } from "../../services/adminApi";
import { Search, Person } from "@mui/icons-material";

// ─── Tipos ────────────────────────────────────────────────────────────────────

type Contato = {
  uid_whatsapp_contato: string;
  wa_num_cliente: string;
  nome_exibicao: string | null;
  etapa_cadastro: string | null;
  syn_cadmom: string;
  ultima_interacao: string | null;
  uid_pesca_pescador: string | null;
  total_mensagens: number;
  ultima_mensagem: string | null;
  nome_usuario: string | null;
  uid_usuario: string | null;
};

type Mensagem = {
  uid_mensagem: string;
  uid_whatsapp_contato: string;
  direcao: "in" | "out";
  conteudo: string | null;
  intent: string | null;
  message_type: string | null;
  syn_cadmom: string;
};

type ChatData = {
  contato: Contato;
  mensagens: Mensagem[];
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatTelefone = (t: string | null) => {
  if (!t) return "—";
  const d = t.replace(/\D/g, "");
  if (d.length === 13) return `+${d.slice(0, 2)} (${d.slice(2, 4)}) ${d.slice(4, 9)}-${d.slice(9)}`;
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  return t;
};

const formatHora = (d: string) =>
  new Date(d).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

const formatDataCurta = (d: string | null) => {
  if (!d) return "—";
  return new Date(d).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
};

const formatDataSeparador = (d: string) =>
  new Date(d).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

const getChaveData = (d: string) => {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${dt.getMonth()}-${dt.getDate()}`;
};

const getNomeContato = (c: Contato | null) => {
  if (!c) return "";
  return c.nome_usuario || c.nome_exibicao || formatTelefone(c.wa_num_cliente);
};

const obterTextoMensagem = (msg: Mensagem) => {
  if (msg.conteudo?.trim()) return msg.conteudo;
  if (msg.message_type && msg.message_type !== "text") return `[${msg.message_type}]`;
  return "[mensagem sem conteúdo]";
};

// ─── Componente ───────────────────────────────────────────────────────────────

const AdminWhatsapp: React.FC = () => {
  const chatRef = useRef<HTMLDivElement | null>(null);
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [selecionado, setSelecionado] = useState<Contato | null>(null);
  const [chatData, setChatData] = useState<ChatData | null>(null);
  const [loadingContatos, setLoadingContatos] = useState(true);
  const [loadingChat, setLoadingChat] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const carregarContatos = useCallback(() => {
    setLoadingContatos(true);
    fetchContatosWhatsapp(debouncedSearch)
      .then((lista) => {
        setContatos(lista);
        if (!selecionado && lista.length > 0) setSelecionado(lista[0]);
      })
      .catch(console.error)
      .finally(() => setLoadingContatos(false));
  }, [debouncedSearch]);

  useEffect(() => { carregarContatos(); }, [carregarContatos]);

  useEffect(() => {
    if (!selecionado) { setChatData(null); return; }
    setLoadingChat(true);
    fetchMensagensContato(selecionado.uid_whatsapp_contato)
      .then(setChatData)
      .catch(console.error)
      .finally(() => setLoadingChat(false));
  }, [selecionado?.uid_whatsapp_contato]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [chatData]);

  const tituloContato = useMemo(() => getNomeContato(selecionado), [selecionado]);

  return (
    <div style={{ padding: "32px 28px", maxWidth: 1200, width: "100%" }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem", fontWeight: 700, color: "#1a2e1e" }}>
          Conversas WhatsApp
        </h1>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: "0.9rem" }}>
          Histórico completo de mensagens dos usuários da plataforma
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, alignItems: "start" }}>
        {/* ── Coluna esquerda: lista de contatos ─── */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #e5ede8", overflow: "hidden",
        }}>
          {/* Header */}
          <div style={{
            padding: "14px 16px", borderBottom: "1px solid #f0f0f0",
            fontWeight: 700, fontSize: "0.8rem",
            color: "#374151", textTransform: "uppercase", letterSpacing: 0.5,
            background: "#f8faf9",
          }}>
            Contatos ({contatos.length})
          </div>

          {/* Busca */}
          <div style={{ padding: "10px 12px", borderBottom: "1px solid #f0f0f0" }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 8,
              background: "#f8f9fa", borderRadius: 8, padding: "8px 12px",
            }}>
              <Search style={{ color: "#9ca3af", fontSize: 18 }} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar contato..."
                style={{ border: "none", outline: "none", fontSize: "0.875rem", width: "100%", background: "transparent" }}
              />
            </div>
          </div>

          {/* Lista */}
          <div style={{ maxHeight: "calc(100vh - 320px)", overflowY: "auto" }}>
            {loadingContatos ? (
              <div style={{ padding: 20, color: "#6b7280", fontSize: "0.875rem" }}>Carregando...</div>
            ) : contatos.length === 0 ? (
              <div style={{ padding: 20, color: "#6b7280", fontSize: "0.875rem" }}>Nenhum contato encontrado.</div>
            ) : (
              contatos.map((c) => {
                const ativo = selecionado?.uid_whatsapp_contato === c.uid_whatsapp_contato;
                const nome = getNomeContato(c);
                return (
                  <button
                    key={c.uid_whatsapp_contato}
                    onClick={() => setSelecionado(c)}
                    style={{
                      width: "100%", border: "none",
                      background: ativo ? "#eef6ff" : "#fff",
                      borderBottom: "1px solid #f0f0f0",
                      padding: "12px 14px", textAlign: "left",
                      cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start",
                      borderLeft: ativo ? "3px solid #3b82f6" : "3px solid transparent",
                      transition: "background 0.12s",
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: 38, height: 38, borderRadius: "50%",
                      background: ativo ? "#dbeafe" : "#e8f4ef",
                      color: ativo ? "#3b82f6" : "#00b37e",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, fontSize: "0.8rem", fontWeight: 700,
                    }}>
                      {nome.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.875rem", color: "#1a2e1e" }}>
                        {nome}
                      </div>
                      <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                        {formatTelefone(c.wa_num_cliente)}
                      </div>
                      {c.ultima_mensagem && (
                        <div style={{
                          fontSize: "0.75rem", color: "#9ca3af",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          maxWidth: 220, marginTop: 2,
                        }}>
                          {c.ultima_mensagem}
                        </div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, textAlign: "right" }}>
                      <div style={{ fontSize: "0.7rem", color: "#9ca3af" }}>
                        {formatDataCurta(c.ultima_interacao)}
                      </div>
                      {c.total_mensagens > 0 && (
                        <div style={{
                          marginTop: 4, background: "#e8f4ef",
                          color: "#00b37e", borderRadius: 10,
                          padding: "1px 7px", fontSize: "0.7rem", fontWeight: 700,
                          display: "inline-block",
                        }}>
                          {c.total_mensagens}
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* ── Coluna direita: chat ─── */}
        <div style={{
          background: "#fff", borderRadius: 14,
          border: "1px solid #e5ede8", overflow: "hidden",
        }}>
          {/* Header do chat */}
          <div style={{
            padding: "14px 20px", borderBottom: "1px solid #f0f0f0",
            background: "#f8faf9", display: "flex",
            justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#1a2e1e" }}>
                {tituloContato || "Selecione um contato"}
              </div>
              {selecionado && (
                <div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                  {formatTelefone(selecionado.wa_num_cliente)}
                  {selecionado.nome_usuario && ` • usuário: ${selecionado.nome_usuario}`}
                </div>
              )}
            </div>
            {selecionado && (
              <div style={{
                background: "#e8f4ef", color: "#00b37e",
                borderRadius: 8, padding: "4px 12px",
                fontSize: "0.75rem", fontWeight: 700,
              }}>
                {chatData?.mensagens.length ?? "—"} mensagens
              </div>
            )}
          </div>

          {/* Área do chat */}
          <div
            ref={chatRef}
            style={{
              height: "calc(100vh - 330px)",
              minHeight: 380,
              overflowY: "auto",
              padding: 20,
              background: "#efeae2",
              display: "flex", flexDirection: "column", gap: 10,
            }}
          >
            {!selecionado ? (
              <div style={{ margin: "auto", background: "#fff", padding: "14px 18px", borderRadius: 8, color: "#666" }}>
                Selecione um contato para ver a conversa.
              </div>
            ) : loadingChat ? (
              <div style={{ margin: "auto", background: "#fff", padding: "14px 18px", borderRadius: 8, color: "#666" }}>
                Carregando mensagens...
              </div>
            ) : !chatData || chatData.mensagens.length === 0 ? (
              <div style={{ margin: "auto", background: "#fff", padding: "14px 18px", borderRadius: 8, color: "#666" }}>
                Nenhuma mensagem encontrada.
              </div>
            ) : (
              chatData.mensagens.map((msg, index) => {
                const inbound = msg.direcao === "in";
                const anterior = index > 0 ? chatData.mensagens[index - 1] : null;
                const mudouDia = !anterior || getChaveData(anterior.syn_cadmom) !== getChaveData(msg.syn_cadmom);

                return (
                  <React.Fragment key={msg.uid_mensagem}>
                    {mudouDia && (
                      <div style={{ display: "flex", justifyContent: "center", margin: "8px 0 2px" }}>
                        <div style={{
                          background: "#fff", color: "#666",
                          fontSize: "0.78rem", padding: "6px 12px",
                          borderRadius: 999, boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                        }}>
                          {formatDataSeparador(msg.syn_cadmom)}
                        </div>
                      </div>
                    )}

                    <div style={{ display: "flex", justifyContent: inbound ? "flex-start" : "flex-end" }}>
                      <div style={{
                        maxWidth: "78%", background: inbound ? "#ffffff" : "#d9fdd3",
                        borderRadius: 8, padding: "10px 12px 18px",
                        position: "relative", boxShadow: "0 1px 1px rgba(0,0,0,0.08)",
                        whiteSpace: "pre-wrap", wordBreak: "break-word",
                        fontSize: "0.93rem", lineHeight: 1.45,
                      }}>
                        <div style={{
                          fontSize: "0.72rem", fontWeight: 600, marginBottom: 4,
                          color: inbound ? "#667781" : "#4f6f52",
                        }}>
                          {inbound ? getNomeContato(selecionado) : "Inteagro"}
                        </div>

                        {msg.message_type && msg.message_type !== "text" && (
                          <div style={{ fontSize: "0.7rem", color: "#888", marginBottom: 4 }}>
                            {msg.message_type === "audio" || msg.message_type === "voice"
                              ? "🎤 Áudio"
                              : `📎 ${msg.message_type}`}
                          </div>
                        )}

                        <div>{obterTextoMensagem(msg)}</div>

                        {msg.intent && (
                          <div style={{
                            marginTop: 6, fontSize: "0.68rem",
                            background: "#f0fdf4", color: "#16a34a",
                            borderRadius: 4, padding: "2px 6px",
                            display: "inline-block",
                          }}>
                            intent: {msg.intent}
                          </div>
                        )}

                        <div style={{
                          position: "absolute", right: 10, bottom: 5,
                          fontSize: "0.68rem", color: "#667781",
                        }}>
                          {formatHora(msg.syn_cadmom)}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminWhatsapp;
